import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  getOnboardingOrders,
  getPlatformConfig,
  setPlatformConfig,
  onboardAutoProvision,
  onboardManualRegister,
  onboardProvision,
  onboardWelcomeCopy,
  onboardWelcomeSend,
  onboardCancel,
  type OnboardAutoProvisionResult,
  type OnboardOrder,
} from "../lib/api";

/* ═══ 開通中心（線 1 onboard 主線）═══
 * 五段健康狀態分別取材：付款時間、tenant_id、Channel 欄位、welcome_sent_at、開業狀態。
 * 訂單 status 只作流程提示，不再單獨代表整張訂單是否健康。 */

const SLA_MS = 2 * 60 * 60 * 1000;
const LANG_LABEL: Record<string, string> = { hk: "繁中", hans: "简体", en: "EN", ms: "MS" };
const STATUS_LABEL: Record<string, string> = {
  awaiting_payment: "🟡 KPay 等付款",
  awaiting_provision: "🟠 待開通",
  queued: "🔵 Channel 隊列中",
  manual_required: "🟠 需人工處理",
  provisioned: "🔵 Channel 已就緒",
  welcome_sent: "🟢 已發歡迎包",
  active: "✅ 已開業",
  cancelled: "⭕ 已取消",
};
const BILLING_CYCLE_LABEL: Record<string, string> = {
  monthly: "月繳",
  quarterly: "季繳（3 個月）",
  half: "半年繳（6 個月）",
  yearly: "年繳（12 個月）",
};
const CHANNEL_READY_STATES = new Set(["ready", "provisioned", "connected", "bound", "active"]);
const PROVISION_FILTER_STATUSES = new Set(["awaiting_provision", "queued", "manual_required", "retryable"]);

type StageTone = "done" | "pending" | "info" | "warning" | "error";

const STAGE_STYLE: Record<StageTone, { section: string; badge: string; marker: string }> = {
  done: {
    section: "border-emerald-800/60 bg-emerald-950/10",
    badge: "bg-emerald-500/15 text-emerald-300",
    marker: "bg-emerald-500/15 text-emerald-300",
  },
  pending: {
    section: "border-slate-800 bg-slate-950/20",
    badge: "bg-slate-800 text-slate-400",
    marker: "bg-slate-800 text-slate-400",
  },
  info: {
    section: "border-sky-800/60 bg-sky-950/10",
    badge: "bg-sky-500/15 text-sky-300",
    marker: "bg-sky-500/15 text-sky-300",
  },
  warning: {
    section: "border-amber-800/60 bg-amber-950/10",
    badge: "bg-amber-500/15 text-amber-300",
    marker: "bg-amber-500/15 text-amber-300",
  },
  error: {
    section: "border-rose-800/60 bg-rose-950/10",
    badge: "bg-rose-500/15 text-rose-300",
    marker: "bg-rose-500/15 text-rose-300",
  },
};

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleString("zh-HK", { hour12: false });
}

function slaInfo(paidAt: number, now: number): { text: string; cls: string; over: boolean } {
  const left = paidAt + SLA_MS - now;
  if (left <= 0) return { text: "已超時", cls: "text-slate-500 bg-slate-800/60", over: true };
  const m = Math.floor(left / 60000);
  const h = Math.floor(m / 60);
  const text = h > 0 ? `剩 ${h} 時 ${m % 60} 分` : `剩 ${m} 分`;
  if (left < 30 * 60 * 1000) {
    return {
      text: `⏰ ${text}`,
      cls: "text-rose-300 bg-rose-500/15 border border-rose-500/40 animate-pulse",
      over: false,
    };
  }
  return { text, cls: "text-amber-300 bg-amber-500/10 border border-amber-500/30", over: false };
}

function sourceLabel(source: string | null): string {
  if (source === "manual") return "人工收款";
  if (source === "kpay_webhook" || source === "kpay_checkout") return "KPay";
  return source || "未註明";
}

function autoOutcome(result?: OnboardAutoProvisionResult) {
  return result?.status ?? result?.state;
}

function Stage({
  index,
  title,
  tone,
  summary,
  children,
}: {
  index: string;
  title: string;
  tone: StageTone;
  summary: string;
  children: ReactNode;
}) {
  const style = STAGE_STYLE[tone];
  return (
    <section className={`rounded-lg border p-3 ${style.section}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`w-6 h-6 inline-flex items-center justify-center rounded-full text-xs font-bold ${style.marker}`}>
          {index}
        </span>
        <h3 className="text-sm font-bold text-slate-200">{title}</h3>
        <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${style.badge}`}>{summary}</span>
      </div>
      <div className="mt-3 text-xs text-slate-400 space-y-2">{children}</div>
    </section>
  );
}

function Detail({ label, value, mono = false }: { label: string; value: ReactNode; mono?: boolean }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <span className="inline-flex gap-1.5 break-words">
      <span className="text-slate-500">{label}：</span>
      <span className={`text-slate-300 ${mono ? "font-mono break-all" : ""}`}>{value}</span>
    </span>
  );
}

export default function OnboardingPage() {
  const [orders, setOrders] = useState<OnboardOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "awaiting_provision" | "welcome_sent" | "active" | "cancelled">("all");
  const [busyOrder, setBusyOrder] = useState<string | null>(null);
  const [toast, setToast] = useState<{ ok: boolean; text: string } | null>(null);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [showManual, setShowManual] = useState(false);
  const [contactNumber, setContactNumber] = useState("");
  const [contactMsg, setContactMsg] = useState("");
  const [apiKeyInput, setApiKeyInput] = useState<Record<string, string>>({});
  const [autoResults, setAutoResults] = useState<Record<string, OnboardAutoProvisionResult>>({});

  const [mName, setMName] = useState("");
  const [mPhone, setMPhone] = useState("");
  const [mPlan, setMPlan] = useState("lite199");
  const [mLang, setMLang] = useState("hk");

  const toastTimer = useRef<number | null>(null);
  const say = useCallback((ok: boolean, text: string) => {
    setToast({ ok, text });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 5000);
  }, []);

  const load = useCallback(async () => {
    try {
      setOrders(await getOnboardingOrders());
      setError("");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "載入失敗");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    getPlatformConfig("onboard_contact_number").then((r: { exists: boolean; value?: string }) => {
      if (r.exists && r.value) setContactNumber(r.value);
    });
    const t = setInterval(load, 30000);
    const clock = setInterval(() => setNow(Date.now()), 30000);
    return () => {
      clearInterval(t);
      clearInterval(clock);
    };
  }, [load]);

  const shown = orders.filter((o) => {
    if (filter === "all") return true;
    if (filter === "awaiting_provision") {
      return PROVISION_FILTER_STATUSES.has(o.status) || o.channel_state === "queued" || o.channel_state === "manual_required";
    }
    if (filter === "welcome_sent") return o.status === "provisioned" || o.status === "welcome_sent";
    return o.status === filter;
  });
  const pending = orders.filter(
    (o) => PROVISION_FILTER_STATUSES.has(o.status) || o.channel_state === "queued" || o.channel_state === "manual_required"
  ).length;
  const activeCount = orders.filter((o) => o.status === "active" || o.onboard_status === "active").length;
  const cancelledCount = orders.filter((o) => o.status === "cancelled").length;

  async function doAutoProvision(order: OnboardOrder) {
    setBusyOrder(order.order_id);
    try {
      const result = await onboardAutoProvision(order.order_id);
      setAutoResults((current) => ({ ...current, [order.order_id]: result }));
      const outcome = autoOutcome(result);
      const detail = result.error || result.message || "";
      if (outcome === "ready" || outcome === "provisioned") {
        say(true, `自動開通完成：${order.customer_name || order.order_id} 的 Channel 已就緒。`);
      } else if (outcome === "queued") {
        say(true, "已加入 Channel 開通隊列；稍後可按「重新對帳」更新狀態。");
      } else if (outcome === "skipped") {
        say(true, "訂單已由其他流程更新，無需重複開通；現正重新載入最新狀態。");
      } else if (outcome === "manual_required") {
        say(false, `此單需人工處理。${detail ? ` ${detail}` : "請檢查 Channel 資料或使用後備手動開通。"}`);
      } else if (result.retryable) {
        say(false, `${detail || "自動開通暫時未能完成"}；可稍後按「重新對帳」。`);
      } else if (detail) {
        say(false, detail);
      } else {
        say(false, "自動開通回應未明，請檢查 Channel 狀態後再試。");
      }
      await load();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "自動開通失敗";
      setAutoResults((current) => ({ ...current, [order.order_id]: { status: "error", error: message } }));
      say(false, message);
    } finally {
      setBusyOrder(null);
    }
  }

  async function doProvision(order: OnboardOrder) {
    const key = (apiKeyInput[order.order_id] || "").trim();
    if (!key) return say(false, "請先貼上租戶的 RPMSMS API Key");
    setBusyOrder(order.order_id);
    try {
      await onboardProvision(order.order_id, key);
      say(true, `已手動開通 ${order.customer_name || order.order_id}；發送號碼為客人電話 ${order.customer_phone}`);
      setApiKeyInput((current) => ({ ...current, [order.order_id]: "" }));
      await load();
    } catch (e: unknown) {
      say(false, e instanceof Error ? e.message : "開通失敗");
    } finally {
      setBusyOrder(null);
    }
  }

  async function doSend(order: OnboardOrder) {
    setBusyOrder(order.order_id);
    try {
      await onboardWelcomeSend(order.order_id);
      say(true, `📤 歡迎包已重發到 ${order.customer_phone}；臨時密碼已重設，舊密碼即時作廢。`);
      await load();
    } catch (e: unknown) {
      say(false, e instanceof Error ? e.message : "發送失敗");
    } finally {
      setBusyOrder(null);
    }
  }

  async function doCopy(order: OnboardOrder) {
    setBusyOrder(order.order_id);
    try {
      const text = await onboardWelcomeCopy(order.order_id);
      try {
        await navigator.clipboard.writeText(text);
        say(true, "📋 歡迎包已複製；臨時密碼已重設，舊密碼即時作廢。請貼到 WhatsApp 發給客人。");
      } catch {
        setCopiedText(text);
        say(true, "📋 剪貼簿被瀏覽器封鎖，請在下方方框人手全選複製。");
      }
      await load();
    } catch (e: unknown) {
      say(false, e instanceof Error ? e.message : "產生歡迎包失敗");
    } finally {
      setBusyOrder(null);
    }
  }

  async function doCancel(order: OnboardOrder) {
    if (
      !window.confirm(
        `確定取消 ${order.order_id}（${order.customer_name || "未填店名"}）？\n取消後不再進行 24h／48h 跟進；租戶資料會保留。`
      )
    ) {
      return;
    }
    setBusyOrder(order.order_id);
    try {
      await onboardCancel(order.order_id);
      say(true, `⭕ 已取消 ${order.order_id}`);
      await load();
    } catch (e: unknown) {
      say(false, e instanceof Error ? e.message : "取消失敗");
    } finally {
      setBusyOrder(null);
    }
  }

  async function doManual(e: React.FormEvent) {
    e.preventDefault();
    const phone = mPhone.replace(/[^0-9]/g, "");
    if (!mName.trim() || phone.length < 6) return say(false, "請填店名及有效電話（含國家碼）");
    setBusyOrder("__manual");
    try {
      const result = await onboardManualRegister({
        customerName: mName.trim(),
        customerPhone: phone,
        plan: mPlan,
        lang: mLang,
      });
      say(true, `已建單 ${result.orderId}（租戶 ${result.tenantId || "未建立"}），ops 已收到通知。`);
      setMName("");
      setMPhone("");
      setShowManual(false);
      await load();
    } catch (e: unknown) {
      say(false, e instanceof Error ? e.message : "建單失敗");
    } finally {
      setBusyOrder(null);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">🧾 開通中心</h1>
          <p className="text-sm text-slate-400 mt-1">
            分開檢查付款、租戶、Channel、歡迎交付及正式開業；自動開通為主，人工後備為輔。
          </p>
        </div>
        <button
          onClick={() => setShowManual((value) => !value)}
          className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold"
        >
          ➕ 人工收款登記
        </button>
      </div>

      {toast && (
        <div
          className={`px-4 py-3 rounded-lg text-sm border ${
            toast.ok
              ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
              : "bg-rose-500/10 border-rose-500/40 text-rose-300"
          }`}
        >
          {toast.text}
        </div>
      )}

      {showManual && (
        <form onSubmit={doManual} className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-3">
          <div>
            <div className="text-sm font-bold text-slate-200">人工收款登記</div>
            <p className="text-xs text-slate-500 mt-1">適用於已在線下收款、沒有 KPay 自動回調的訂單。</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <input
              value={mName}
              onChange={(e) => setMName(e.target.value)}
              placeholder="店名"
              className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm"
            />
            <input
              value={mPhone}
              onChange={(e) => setMPhone(e.target.value)}
              placeholder="電話（852…）"
              inputMode="numeric"
              className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm"
            />
            <select
              value={mPlan}
              onChange={(e) => setMPlan(e.target.value)}
              className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm"
            >
              <option value="lite199">Lite</option>
              <option value="pro499">Pro</option>
            </select>
            <select
              value={mLang}
              onChange={(e) => setMLang(e.target.value)}
              className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm"
            >
              <option value="hk">繁中</option>
              <option value="hans">简体</option>
              <option value="en">EN</option>
              <option value="ms">MS</option>
            </select>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="submit"
              disabled={busyOrder === "__manual"}
              className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-bold"
            >
              {busyOrder === "__manual" ? "建單中…" : "建單＋通知 ops"}
            </button>
            <span className="text-xs text-slate-500">建單會建立租戶並經 WhatsApp 通知 ops（2 小時 SLA 起跳）。</span>
          </div>
        </form>
      )}

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center gap-3 flex-wrap">
        <span className="text-sm text-slate-300">📞 平台聯絡號碼（用於歡迎包「任何問題」一句）</span>
        <input
          value={contactNumber}
          onChange={(e) => setContactNumber(e.target.value.replace(/[^0-9]/g, ""))}
          placeholder="85268810677"
          className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm font-mono w-44"
          inputMode="numeric"
        />
        <button
          onClick={async () => {
            if (!contactNumber.trim()) return;
            setContactMsg("儲存中…");
            try {
              await setPlatformConfig("onboard_contact_number", contactNumber.trim());
              setContactMsg("✅ 已儲存；之後的歡迎包會使用這個號碼。");
            } catch {
              setContactMsg("❌ 儲存失敗");
            }
          }}
          className="px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-bold"
        >
          儲存
        </button>
        {contactMsg && <span className="text-xs text-slate-400">{contactMsg}</span>}
      </div>

      <div className="flex gap-2 flex-wrap text-sm">
        {(
          [
            ["all", `全部 ${orders.length}`],
            ["awaiting_provision", `待開通 ${pending}`],
            ["welcome_sent", `已發送 ${orders.filter((o) => o.status === "provisioned" || o.status === "welcome_sent").length}`],
            ["active", `已開業 ${activeCount}`],
            ["cancelled", `已取消 ${cancelledCount}`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-3 py-1.5 rounded-full border ${
              filter === key
                ? "bg-emerald-600/20 border-emerald-600/40 text-emerald-300"
                : "border-slate-700 text-slate-400 hover:text-slate-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && <div className="text-slate-400">載入中…</div>}
      {error && <div className="text-rose-400">{error}</div>}
      {!loading && !error && shown.length === 0 && (
        <div className="text-slate-500 py-8 text-center border border-dashed border-slate-800 rounded-xl">
          沒有符合條件的訂單
        </div>
      )}

      <div className="space-y-3">
        {shown.map((o) => {
          const sla = o.status === "awaiting_provision" && o.paid_at ? slaInfo(o.paid_at, now) : null;
          const busy = busyOrder === o.order_id;
          const autoResult = autoResults[o.order_id];
          const hasServerChannelOutcome = Boolean(o.channel_state) || o.status !== "awaiting_provision";
          const outcome = hasServerChannelOutcome ? undefined : autoOutcome(autoResult);
          const channelState = o.channel_state || autoResult?.channel_state || autoResult?.channelState || "";
          const normalizedChannelState = channelState.toLowerCase();
          const manualRequired =
            outcome === "manual_required" || o.status === "manual_required" || normalizedChannelState === "manual_required";
          const queued =
            !manualRequired &&
            (outcome === "queued" || o.status === "queued" || normalizedChannelState === "queued" || normalizedChannelState === "provisioning");
          const retryable =
            !manualRequired &&
            ((!hasServerChannelOutcome && autoResult?.retryable === true) || o.status === "retryable" || normalizedChannelState === "retryable" || normalizedChannelState === "error");
          const readyByResult = outcome === "ready" || outcome === "provisioned";
          const channelReady = o.bound === 1 || readyByResult || CHANNEL_READY_STATES.has(normalizedChannelState);
          const channelError = hasServerChannelOutcome ? o.channel_last_error || "" : autoResult?.error || o.channel_last_error || "";
          const paid = o.status !== "awaiting_payment" && Boolean(o.paid_at);
          const welcomeDelivered =
            Boolean(o.welcome_sent_at) || o.status === "welcome_sent" || o.status === "active" || o.onboard_status === "active";
          const officiallyActive = o.status === "active" || o.onboard_status === "active";
          const cancelled = o.status === "cancelled";
          const isOpen =
            expanded === o.order_id ||
            (expanded === null && (o.status === "awaiting_provision" || queued || retryable || manualRequired));
          const steps = {
            kb: (o.kb_chunk_count ?? 0) > 0,
            bound: o.bound === 1,
            test: (o.bot_tests ?? 0) > 0,
          };
          const canUseProvision =
            Boolean(o.tenant_id) &&
            !channelReady &&
            !cancelled &&
            (o.status === "awaiting_provision" || queued || retryable || manualRequired);
          const canAutoProvision = canUseProvision && !manualRequired;
          const canDeliverWelcome =
            Boolean(o.tenant_id) &&
            channelReady &&
            Boolean(o.api_key_set_at || readyByResult || o.welcome_sent_at) &&
            !officiallyActive &&
            !cancelled &&
            o.status !== "awaiting_payment";
          const channelSummary = cancelled
            ? "已取消"
            : manualRequired
              ? "需人工處理"
              : channelReady
                ? "已就緒"
                : queued
                  ? "隊列／對帳中"
                  : retryable
                    ? "可重試"
                    : channelError
                      ? "開通異常"
                      : o.tenant_id
                        ? "待開通"
                        : "等待租戶";
          const channelTone: StageTone = cancelled
            ? "pending"
            : manualRequired || (!channelReady && channelError)
              ? "error"
              : channelReady
                ? "done"
                : queued || retryable
                  ? "info"
                  : "pending";
          const partner = o.partner_name
            ? `${o.partner_name}${o.partner_id ? `（${o.partner_id}）` : ""}`
            : o.partner_id;

          return (
            <div key={o.order_id} className={`bg-slate-900 border rounded-xl p-4 ${sla?.over ? "border-slate-800 opacity-70" : "border-slate-800"}`}>
              <button
                className="w-full text-left flex items-center gap-3 flex-wrap"
                onClick={() => setExpanded(isOpen ? "__closed__" : o.order_id)}
              >
                <span className="font-bold text-slate-100">{o.customer_name || "（未填店名）"}</span>
                <span className="text-xs text-slate-500 font-mono">{o.order_id}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300">
                  {o.plan === "pro499" ? "Pro" : o.plan === "lite199" ? "Lite" : o.plan}
                </span>
                <span className="text-xs text-slate-500">
                  {LANG_LABEL[o.lang] || o.lang} · {sourceLabel(o.source)}
                </span>
                <span className="ml-auto flex items-center gap-2 flex-wrap">
                  {paid && o.paid_at > 0 && <span className="text-xs text-slate-500">付款 {fmtTime(o.paid_at)}</span>}
                  {sla && (
                    <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${sla.cls}`}>
                      {sla.over ? "⌛ 已超時" : sla.text}
                    </span>
                  )}
                  <span className="text-xs px-2.5 py-1 rounded-full bg-slate-800 text-slate-300">
                    {STATUS_LABEL[o.status] || o.status}
                  </span>
                </span>
              </button>

              {isOpen && (
                <div className="mt-4 pt-4 border-t border-slate-800 space-y-3">
                  <Stage index="1" title="付款" tone={paid ? "done" : cancelled ? "pending" : "warning"} summary={paid ? "已付款" : cancelled ? "已取消" : "等待付款"}>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {paid && o.paid_at > 0 && <Detail label="付款時間" value={fmtTime(o.paid_at)} />}
                      <Detail label="Billing cycle" value={o.billing_cycle ? BILLING_CYCLE_LABEL[o.billing_cycle] || o.billing_cycle : null} />
                      <Detail label="金額" value={o.amount !== null && o.amount !== undefined ? `HK$${o.amount}` : null} />
                      <Detail label="Managed order no." value={o.managed_order_no} mono />
                      <Detail label="Transaction no." value={o.transaction_no} mono />
                    </div>
                    {!paid && !cancelled && <p>付款確認後才進入租戶及 Channel 開通。</p>}
                  </Stage>

                  <Stage index="2" title="租戶" tone={o.tenant_id ? "done" : cancelled ? "pending" : "warning"} summary={o.tenant_id ? "已建立" : cancelled ? "已取消" : "未建立"}>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      <Detail label="Tenant ID" value={o.tenant_id} mono />
                      <Detail label="方案" value={o.plan === "pro499" ? "Pro" : o.plan === "lite199" ? "Lite" : o.plan} />
                      <Detail label="語言" value={LANG_LABEL[o.lang] || o.lang} />
                      <Detail label="來源" value={sourceLabel(o.source)} />
                      <Detail label="Partner" value={partner} />
                      <Detail label="推薦碼" value={o.ref_code} mono />
                    </div>
                    {!o.tenant_id && !cancelled && <p>尚未有 tenant_id，請先核對付款及建戶資料，不應直接發送歡迎包。</p>}
                  </Stage>

                  <Stage index="3" title="Channel" tone={channelTone} summary={channelSummary}>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      <Detail label="channel_state" value={channelState || "未回報"} mono />
                      <Detail label="Channel ID" value={o.channel_id} mono />
                      <Detail label="Channel username" value={o.channel_username} />
                      <Detail label="嘗試次數" value={o.channel_attempts ?? null} />
                      <Detail label="處理租約至" value={o.channel_lease_until ? fmtTime(o.channel_lease_until) : null} />
                    </div>
                    {channelError && (
                      <div className="rounded bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-rose-300 whitespace-pre-wrap break-words">
                        Channel 最後錯誤：{channelError}
                      </div>
                    )}
                    {manualRequired && !cancelled && (
                      <div className="rounded bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-amber-200">
                        此單需人工處理。請核對 Channel 錯誤與租戶資料；如自動流程不適用，可使用下方 RPMSMS Key 後備開通。
                      </div>
                    )}
                    {canAutoProvision && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <button
                          onClick={() => doAutoProvision(o)}
                          disabled={busy}
                          className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-bold"
                        >
                          {busy ? (queued || retryable ? "對帳中…" : "開通中…") : queued || retryable ? "🔄 重新對帳" : "⚡ 自動開通"}
                        </button>
                        <span className="text-xs text-slate-500">
                          {queued || retryable ? "重新讀取 Channel 結果，不重複建租戶。" : "使用現有 tenant_id 執行自動 Channel 開通。"}
                        </span>
                      </div>
                    )}
                    {canUseProvision && (
                      <details className="rounded-lg border border-slate-800 bg-slate-950/30 px-3 py-2">
                        <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-200">
                          後備：手動貼 RPMSMS Key
                        </summary>
                        <div className="mt-3 space-y-2">
                          <p className="text-xs text-slate-500">
                            僅供自動開通不適用或需人工接管時使用；發送號碼會設為客人電話 {o.customer_phone}。
                          </p>
                          <div className="flex gap-2 flex-wrap">
                            <input
                              type="password"
                              value={apiKeyInput[o.order_id] || ""}
                              onChange={(e) => setApiKeyInput((current) => ({ ...current, [o.order_id]: e.target.value }))}
                              placeholder="貼 RPMSMS API Key"
                              className="flex-1 min-w-[240px] px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm font-mono"
                              autoComplete="off"
                            />
                            <button
                              onClick={() => doProvision(o)}
                              disabled={busy}
                              className="px-4 py-2 rounded-lg border border-sky-700 hover:border-sky-500 disabled:opacity-50 text-sky-300 text-sm"
                            >
                              {busy ? "處理中…" : "🔑 手動開通"}
                            </button>
                          </div>
                        </div>
                      </details>
                    )}
                  </Stage>

                  <Stage
                    index="4"
                    title="歡迎交付"
                    tone={welcomeDelivered ? "done" : cancelled ? "pending" : channelReady ? "warning" : "pending"}
                    summary={welcomeDelivered ? "已交付" : cancelled ? "已取消" : "待交付"}
                  >
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      <Detail label="交付時間" value={o.welcome_sent_at ? fmtTime(o.welcome_sent_at) : null} />
                      <Detail label="接收電話" value={o.customer_phone} mono />
                    </div>
                    {canDeliverWelcome ? (
                      <div className="space-y-2">
                        <div className="flex gap-2 flex-wrap items-center">
                          <button
                            onClick={() => doSend(o)}
                            disabled={busy}
                            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-bold"
                          >
                            {busy ? "處理中…" : "📤 重發歡迎包"}
                          </button>
                          <button
                            onClick={() => doCopy(o)}
                            disabled={busy}
                            className="px-4 py-2 rounded-lg border border-slate-700 hover:border-slate-500 disabled:opacity-50 text-slate-300 text-sm"
                          >
                            📋 複製歡迎包（後備）
                          </button>
                        </div>
                        <p className="text-xs text-amber-300">
                          重發歡迎包會重設臨時密碼，舊密碼即時作廢；使用後備複製同樣會重設臨時密碼。
                        </p>
                      </div>
                    ) : (
                      !welcomeDelivered && !cancelled && <p>Channel 就緒並完成 provision 後，才可發送歡迎包。</p>
                    )}
                  </Stage>

                  <Stage
                    index="5"
                    title="正式開業"
                    tone={officiallyActive ? "done" : cancelled ? "pending" : "warning"}
                    summary={officiallyActive ? "已開業" : cancelled ? "已取消" : "待客人完成"}
                  >
                    <div className="flex gap-2 flex-wrap text-xs">
                      <span className={`px-2 py-1 rounded ${steps.kb ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-800 text-slate-400"}`}>
                        {steps.kb ? "✓" : "○"} ①教 Bot（KB {o.kb_chunk_count ?? 0} 塊）
                      </span>
                      <span className={`px-2 py-1 rounded ${steps.bound ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-800 text-slate-400"}`}>
                        {steps.bound ? "✓" : "○"} ②綁定
                      </span>
                      <span className={`px-2 py-1 rounded ${steps.test ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-800 text-slate-400"}`}>
                        {steps.test ? "✓" : "○"} ③試玩（{o.bot_tests ?? 0} 次）
                      </span>
                    </div>
                    <p className={officiallyActive ? "text-emerald-300" : "text-slate-500"}>
                      {officiallyActive ? "🎉 客人已完成開業流程。" : cancelled ? "訂單已取消，租戶資料保留。" : "客人完成教 Bot、綁定及試玩後，才視為正式開業。"}
                    </p>
                  </Stage>

                  {!officiallyActive && !cancelled && (
                    <div className="flex items-center gap-3 flex-wrap pt-1">
                      <button
                        onClick={() => doCancel(o)}
                        disabled={busy}
                        className="px-3 py-1.5 rounded-lg border border-rose-500/40 hover:border-rose-400 disabled:opacity-50 text-rose-300 text-xs"
                      >
                        ✖️ 取消此單
                      </button>
                      <span className="text-xs text-slate-600">用於超時或問題單；取消後不再跟進，租戶資料保留。</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {copiedText && (
        <div className="fixed bottom-6 right-6 z-50 max-w-md bg-slate-900 border border-emerald-600/40 rounded-xl p-4 shadow-2xl">
          <div className="text-xs text-emerald-300 mb-2 font-bold">📋 人手複製區（請全選後按 Ctrl+C）</div>
          <pre className="text-xs text-slate-200 whitespace-pre-wrap max-h-64 overflow-auto">{copiedText}</pre>
          <button onClick={() => setCopiedText(null)} className="mt-2 text-xs text-slate-400 hover:text-slate-200">
            關閉
          </button>
        </div>
      )}
    </div>
  );
}
